import {
	type UseMutationOptions,
	type UseMutationResult,
	useMutation,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { parseCapacityExceededError } from "@/utils/capacityError";

interface ApiErrorResponse {
	error?: string;
	code?: string;
	message?: string;
}

export interface ToastMessages {
	loading?: string;
	success?: string;
	error?: string;
}

interface ContextWithToast {
	toastId?: string | number;
}

export interface MutationWithToastOptions<TData, TError, TVariables, TContext>
	extends Omit<
		UseMutationOptions<TData, TError, TVariables, TContext>,
		"onMutate" | "onSuccess" | "onError"
	> {
	toastMessages?: ToastMessages;
	onMutate?: (
		variables: TVariables,
	) => Promise<TContext> | TContext | undefined;
	onSuccess?: (
		data: TData,
		variables: TVariables,
		context: TContext | undefined,
	) => Promise<unknown> | unknown;
	onError?: (
		error: TError,
		variables: TVariables,
		context: TContext | undefined,
	) => Promise<unknown> | unknown;
	showLoadingToast?: boolean;
	showSuccessToast?: boolean;
	showErrorToast?: boolean;
}

/**
 * Custom hook that wraps useMutation with automatic toast notifications
 * Provides loading, success, and error toasts with i18n support
 */
export function useMutationWithToast<
	TData = unknown,
	TError = Error,
	TVariables = void,
	TContext = unknown,
>(
	options: MutationWithToastOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
	const { t } = useTranslation();

	const {
		toastMessages = {},
		onMutate,
		onSuccess,
		onError,
		showLoadingToast = true,
		showSuccessToast = true,
		showErrorToast = true,
		...mutationOptions
	} = options;

	return useMutation({
		...mutationOptions,
		onMutate: async (variables) => {
			let toastId: string | number | undefined;

			// Show loading toast if enabled
			if (showLoadingToast && toastMessages.loading) {
				toastId = toast.loading(toastMessages.loading);
			}

			// Call original onMutate if provided
			const context = await onMutate?.(variables);

			// Return context with toastId for cleanup
			return { ...context, toastId } as TContext & ContextWithToast;
		},
		onSuccess: async (data, variables, context) => {
			// Dismiss loading toast if it exists
			const contextWithToast = context as ContextWithToast;
			if (contextWithToast?.toastId) {
				toast.dismiss(contextWithToast.toastId);
			}

			// Show success toast if enabled
			if (showSuccessToast && toastMessages.success) {
				toast.success(toastMessages.success);
			}

			// Call original onSuccess if provided
			await onSuccess?.(data, variables, context);
		},
		onError: async (error, variables, context) => {
			// Dismiss loading toast if it exists
			const contextWithToast = context as ContextWithToast;
			if (contextWithToast?.toastId) {
				toast.dismiss(contextWithToast.toastId);
			}

			// Show error toast if enabled
			if (showErrorToast) {
				// Check for plan limit exceeded error (403 with LIMIT_EXCEEDED code)
				const axiosError = error as AxiosError<ApiErrorResponse>;
				const errorData = axiosError.response?.data;

				// Capacity-exceeded 409 -> clear, scope-aware message with counts.
				const capacityError = parseCapacityExceededError(error);

				if (capacityError) {
					toast.error(
						t(
							capacityError.scope === "session"
								? "common.errors.capacityExceededSession"
								: "common.errors.capacityExceededSchedule",
							{
								current: capacityError.current,
								capacity: capacityError.capacity,
							},
						),
					);
				} else if (
					axiosError.response?.status === 403 &&
					errorData?.code === "LIMIT_EXCEEDED"
				) {
					toast.error(t("common.planLimitExceeded"));
				} else {
					const errorMessage =
						toastMessages.error || t("common.errors.generic");
					toast.error(errorMessage);
				}
			}

			// Call original onError if provided
			await onError?.(error, variables, context);
		},
	});
}

/**
 * Predefined toast message generators for common operations
 */
export const createToastMessages = {
	create: (_entityName?: string) => ({
		loading: "common.toasts.creating",
		success: "common.toasts.createSuccess",
		error: "common.toasts.createError",
	}),
	update: (_entityName?: string) => ({
		loading: "common.toasts.updating",
		success: "common.toasts.updateSuccess",
		error: "common.toasts.updateError",
	}),
	delete: (_entityName?: string) => ({
		loading: "common.toasts.deleting",
		success: "common.toasts.deleteSuccess",
		error: "common.toasts.deleteError",
	}),
	invite: () => ({
		loading: "common.toasts.inviting",
		success: "common.toasts.inviteSuccess",
		error: "common.toasts.inviteError",
	}),
	accept: () => ({
		loading: "common.toasts.accepting",
		success: "common.toasts.acceptSuccess",
		error: "common.toasts.acceptError",
	}),
	revoke: () => ({
		loading: "common.toasts.revoking",
		success: "common.toasts.revokeSuccess",
		error: "common.toasts.revokeError",
	}),
};

/**
 * Utility function to create toast messages with entity-specific translations
 */
export function createEntityToastMessages(
	operation: keyof typeof createToastMessages,
	entityKey: string,
) {
	const baseMessages = createToastMessages[operation](entityKey);
	return {
		loading: baseMessages.loading.replace(
			"common.toasts",
			`${entityKey}.toasts`,
		),
		success: baseMessages.success.replace(
			"common.toasts",
			`${entityKey}.toasts`,
		),
		error: baseMessages.error.replace("common.toasts", `${entityKey}.toasts`),
	};
}
